// frontend/src/pages/LandingPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import FeaturedCompanies from '../components/LandingPage/FeaturedCompanies'; // Import the new component
import { motion } from 'framer-motion';

// Helper for smooth scroll
const smoothScrollTo = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    } else {
        console.warn(`Smooth scroll target '${elementId}' not found.`);
    }
};

// --- Hero Section Component ---
const HeroSection = () => {
    const { isAuthenticated } = useAuth();
    const heroGradientTextStyle = {
        background: 'linear-gradient(to right, #2563eb, #1e3a8a)', // Adjusted to darker blues: blue-600 to blue-800
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        color: '#2563eb', 
    };

    return (
        <header id="home" className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100"> {/* Subtle gradient background */}
            <div className="container mx-auto px-6 text-center">
                <motion.h1 
                    className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    AI Insights, <span style={heroGradientTextStyle}>Simplified.</span>
                </motion.h1>
                <motion.p 
                    className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto mb-10" // Slightly increased text color contrast
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                >
                    Stay ahead with curated AI news. Agent Digest filters, classifies, and delivers key updates. Effortless intelligence.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6"
                >
                    {isAuthenticated ? (
                        <Link
                            to="/article-feed"
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition duration-150 ease-in-out shadow-lg hover:shadow-xl text-base transform hover:scale-105"
                        >
                            View Article Feed
                        </Link>
                    ) : (
                        <>
                            <Link
                                to="/register"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition duration-150 ease-in-out shadow-lg hover:shadow-xl text-base transform hover:scale-105"
                            >
                                Get Started Free
                            </Link>
                            <Link
                                to="/login"
                                className="text-blue-600 hover:text-blue-700 font-semibold py-3 px-8 rounded-lg transition duration-150 ease-in-out border-2 border-blue-600 hover:bg-blue-50 text-base transform hover:scale-105"
                            >
                                Log In
                            </Link>
                        </>
                    )}
                </motion.div>

                {/* Optional: Placeholder for a product screenshot or a subtle graphic later */}
                {/* 
                <motion.div 
                    className="max-w-3xl mx-auto mt-16 mb-8"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                >
                    <div
                        className="bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden"
                    >
                         <img src="/placeholder-app-screenshot.png" alt="App Screenshot" className="w-full h-auto" />
                    </div>
                </motion.div>
                */}
            </div>
        </header>
    );
};

// --- Content Section Component (Reusable) ---
const ContentSection = ({ id, title, children, bgColor = "bg-white", titleSize = "text-3xl" }) => {
    return (
        <section id={id} className={`py-16 md:py-20 ${bgColor}`}> {/* Reduced vertical padding slightly */}
            <div className="container mx-auto px-6">
                <h2 className={`${titleSize} font-bold mb-10 text-gray-800 text-center`}>{title}</h2>
                <div className="text-left md:text-lg text-gray-700 max-w-3xl mx-auto space-y-5"> {/* Increased base text size and spacing */}
                    {children}
                </div>
            </div>
        </section>
    );
};


// --- Features Section ---
const FeatureCard = ({ icon, title, description, delay }) => (
    <motion.div 
        className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: delay }}
        viewport={{ once: true, amount: 0.3 }}
    >
        <div className="p-3 bg-blue-100 rounded-full mb-4 text-blue-600">
            {React.cloneElement(icon, { className: "w-8 h-8" })}
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </motion.div>
);

const FeaturesSection = () => {
    // Icons (ensure you have these or similar from heroicons or another library)
    const FilterIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2s2-.9 2-2V5c0-1.1-.9-2-2-2zm0 0c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2s2-.9 2-2V5c0-1.1-.9-2-2-2zm0 0M6 7h12M6 12h12M6 17h12" /></svg>; // Simplified funnel/filter
    const ClassifyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a15.995 15.995 0 00-4.764 4.648l-3.876 5.814a1.151 1.151 0 001.597 1.597l5.814-3.875a15.996 15.996 0 004.649-4.763m-3.42-3.42a15.995 15.995 0 004.764-4.648" /></svg>; // Tag/classify
    const DeliverIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" /></svg>; // Digest/feed

    const features = [
        { icon: <FilterIcon />, title: "Smart Curation", description: "AI agents gather and sift through global AI news, bringing you only what's relevant.", delay: 0.1 },
        { icon: <ClassifyIcon />, title: "Intelligent Tagging", description: "Articles are automatically categorized by topics, tech level, and content type for easy filtering.", delay: 0.2 },
        { icon: <DeliverIcon />, title: "Personalized Feeds", description: "Your custom dashboard highlights news matching your interests. Daily digests keep you updated.", delay: 0.3 },
    ];

    return (
        <section id="features" className="py-16 md:py-24 bg-slate-50">
            <div className="container mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
                        How Agent Digest Works
                    </h2>
                    <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12 md:mb-16">
                        We leverage AI to streamline your access to AI advancements. Focus on insights, not information overload.
                    </p>
                </motion.div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {features.map((feature, index) => (
                        <FeatureCard key={index} {...feature} />
                    ))}
                </div>
            </div>
        </section>
    );
};

// --- Call to Action Section ---
const CallToActionSection = () => (
    <section id="cta" className="py-16 md:py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 text-gray-900"> {/* Changed background */}
        <div className="container mx-auto px-6 text-center">
            <motion.h2 
                className="text-3xl md:text-4xl font-bold mb-6 text-blue-700" // Emphasize with blue
                initial={{ opacity: 0, y: -20 }} // Animate from top
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, type: 'spring', stiffness: 100 }}
                viewport={{ once: true, amount: 0.3 }}
            >
                Ready to Dive In?
            </motion.h2>
            <motion.p 
                className="text-lg md:text-xl max-w-xl mx-auto mb-10 text-gray-700" // Adjusted text color for new background
                initial={{ opacity: 0, y: 20 }} // Animate from bottom
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2, type: 'spring', stiffness: 100 }}
                viewport={{ once: true, amount: 0.3 }}
            >
                Join Agent Digest today and transform how you consume AI news. Stay informed, effortlessly.
            </motion.p>
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true, amount: 0.3 }}
            >
                <Link
                    to="/register"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-10 rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out text-lg transform hover:scale-105"
                >
                    Sign Up for Free
                </Link>
            </motion.div>
        </div>
    </section>
);

// --- Footer Component ---
const Footer = () => {
    const currentYear = new Date().getFullYear();
    return (
        <footer className="bg-slate-100 text-slate-700 py-12"> {/* Changed background to slate-100, text to slate-700 */}
            <div className="container mx-auto px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8"> {/* Simplified to 2 columns */}
                    <div>
                        <h5 className="text-xl font-semibold text-slate-800 mb-3">Agent Digest</h5> {/* Adjusted text color */}
                        <p className="text-sm">AI-powered news aggregation and classification. Stay informed, effortlessly.</p> {/* More concise description */}
                    </div>
                    <div>
                        <h5 className="text-lg font-semibold text-slate-800 mb-3">Quick Links</h5> {/* Adjusted text color */}
                        <ul className="space-y-2 text-sm">
                            <li><a href="#home" onClick={(e) => { e.preventDefault(); smoothScrollTo('home'); }} className="hover:text-blue-600 transition-colors">Home</a></li>
                            <li><a href="#features" onClick={(e) => { e.preventDefault(); smoothScrollTo('features'); }} className="hover:text-blue-600 transition-colors">How It Works</a></li>
                            <li><a href="#cta" onClick={(e) => { e.preventDefault(); smoothScrollTo('cta'); }} className="hover:text-blue-600 transition-colors">Get Started</a></li>
                            {/* Removed Sources and Legal sections */}
                        </ul>
                    </div>
                </div>
            </div>
        </footer>
    );
};


// --- Main Landing Page Component ---
const LandingPage = () => {
    // Effect to ensure Inter font is loaded (if not globally handled by index.css)
    // and set body font family. It's better to set this in index.css.
    useEffect(() => {
        const interFontLink = document.createElement('link');
        interFontLink.href = "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap";
        interFontLink.rel = "stylesheet";
        
        // Check if the link already exists to avoid duplicates
        if (!document.querySelector(`link[href="${interFontLink.href}"]`)) {
            document.head.appendChild(interFontLink);
        }

        // It's generally better to set body font in index.css or App.css
        // document.body.style.fontFamily = "'Inter', sans-serif";

        // Clean up by removing the link if it was added by this component
        return () => {
            // This cleanup might be too aggressive if other components also add it.
            // If index.css handles font loading, this useEffect isn't strictly necessary for the font link.
            // const existingLink = document.querySelector(`link[href="${interFontLink.href}"]`);
            // if (existingLink && document.head.contains(existingLink)) {
            //     document.head.removeChild(existingLink);
            // }
        };
    }, []);


    return (
        // Added font-sans from Tailwind to apply 'Inter' if configured in tailwind.config.js
        <div className="flex flex-col min-h-screen bg-white text-gray-800 font-sans"> 
            {/* <Navbar /> Removed, LandingNavbar will be imported in App.js or a layout component */}
            <main className="flex-grow pt-16"> {/* Add padding-top if Navbar is fixed and height is known e.g. pt-16 or pt-20 */}
                <HeroSection />
                <FeaturedCompanies /> {/* This is the new scrolling companies section */}
                <FeaturesSection />
                {/* <SourcesSectionLanding /> Re-enable or implement this section as needed */}
                {/* <TestimonialsSection /> */}
                <CallToActionSection />
            </main>
            <Footer /> {/* This uses the Footer defined within this file */}
        </div>
    );
};

export default LandingPage;

// Add motion import if not already present at the top
// import { motion } from 'framer-motion';
// (It should be there from previous edits, but as a reminder)
// Make sure React is imported: import React from 'react';
// Make sure Link is imported from react-router-dom
